import { styled } from '@mui/material/styles';
import { ToggleButton, toggleButtonClasses, type ToggleButtonProps } from '@mui/material';
import {
  Button as FplButton,
  type ButtonProps as FplButtonProps,
  DialogTriggerButton,
  ToggleButton as FplToggleButton,
} from '@figma/fpl-components';
import { forwardRef, type MouseEvent } from 'react';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { color } from '@/colors';
import { FF_FIGMA_THEME } from '@/consts/featureFlags';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useIsFigmaEmbedMode } from '@/contexts/FigmaEmbedModeContext';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { getFplDataAttributes } from '@/UI/Buttons/fplDataAttributes';

export type AppToggleButtonMode = 'dark' | 'light' | 'contained' | 'group';

export interface AppToggleButtonProps extends ToggleButtonProps {
  isFigmaTheme?: boolean;
  mode?: AppToggleButtonMode;
  btnW?: string | number;
  btnH?: string | number;
  isIcon?: boolean;
  svgSize?: string | number;
  iconPrefix?: FplButtonProps['iconPrefix'];
}

interface StyledAppToggleButtonProps extends AppToggleButtonProps {
  isFigmaThemeResolved: boolean;
}

const StyledAppToggleButton = styled(ToggleButton, {
  shouldForwardProp: (prop) =>
    // oxlint-disable-next-line typescript/no-unnecessary-type-assertion -- MUI types this prop as PropertyKey.
    !['mode', 'btnW', 'btnH', 'isIcon', 'svgSize', 'isFigmaTheme', 'isFigmaThemeResolved'].includes(prop as string),
})<StyledAppToggleButtonProps>(({ theme, mode = 'dark', btnW, btnH, isIcon, svgSize = 20, isFigmaThemeResolved }) => {
  if (mode === 'contained') {
    return {
      flex: '1 1 auto',
      height: '100%',
      cursor: 'pointer',
      fontSize: '0.75rem',
      backgroundColor: color.Black88,
      color: color.White40_T,
      margin: 0,
      marginLeft: 0,
      padding: 0,
      border: 'none !important',
      '&:hover': {
        backgroundColor: color.Black84,
      },
      [`&.${toggleButtonClasses.selected}`]: {
        backgroundColor: color.Black84,
        color: token.text,
        '&:hover': {
          backgroundColor: color.Black84,
        },
      },
      [`&.${toggleButtonClasses.disabled}`]: {
        backgroundColor: color.Black08,
        color: color.Black40,
      },
    };
  }
  if (mode === 'group') {
    return {
      height: btnH || '20px',
      width: btnW || 'auto',
      padding: theme.spacing(0.75),
      '& svg': {
        width: svgSize,
        height: svgSize,
      },
    };
  }
  if (mode === 'light') {
    return {
      height: btnH || '20px',
      width: btnW || 'auto',
      padding: isIcon ? theme.spacing(0.25) : `${theme.spacing(0.25)} ${theme.spacing(1)}`,
      cursor: 'pointer',
      fontSize: '0.75rem',
      color: isFigmaThemeResolved ? color.White80_T : color.White64_T,
      minWidth: btnW || 'auto',
      [`&.${toggleButtonClasses.root}`]: {
        margin: '0 1px',
      },
      '&:hover': {
        backgroundColor: isFigmaThemeResolved ? color.White08_T : color.Black84,
        color: isFigmaThemeResolved ? token.text : color.White64_T,
        '& svg': {
          color: isFigmaThemeResolved ? token.text : color.White80_T,
        },
      },
      [`&.${toggleButtonClasses.selected}`]: {
        color: color.Black92,
        backgroundColor: isFigmaThemeResolved ? color.White100 : color.Yellow100,
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: isFigmaThemeResolved ? color.White80_T : color.Yellow64,
        },
        '& svg': {
          color: color.Black92,
        },
      },
      '& svg': {
        width: svgSize,
        height: svgSize,
        color: color.White80_T,
      },
      ...(isFigmaThemeResolved
        ? {
            [`&.${toggleButtonClasses.disabled}`]: {
              color: color.White40_T,
              backgroundColor: 'transparent',
            },
          }
        : {}),
      [`&.${toggleButtonClasses.disabled} svg`]: {
        color: color.White40_T,
      },
    };
  }
  // default to dark
  return {
    height: btnH || '20px',
    width: btnW || 'auto',
    padding: isIcon ? theme.spacing(0.25) : `${theme.spacing(0.25)} ${theme.spacing(1)}`,
    cursor: 'pointer',
    fontSize: '0.75rem',
    color: color.White64_T,
    minWidth: btnW || 'auto',
    [`&.${toggleButtonClasses.root}`]: {
      margin: '0 1px',
    },
    '&:hover': {
      backgroundColor: color.Black84,
      color: color.White64_T,
      '& svg': {
        color: token.text,
      },
    },
    [`&.${toggleButtonClasses.selected}`]: {
      color: token.text,
      backgroundColor: color.Black84,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: color.Black84,
      },
      '& svg': {
        color: token.text,
      },
    },
    '& svg': {
      width: svgSize,
      height: svgSize,
      color: color.White80_T,
    },
    [`&.${toggleButtonClasses.disabled} svg`]: {
      color: color.White40_T,
    },
  };
});

function invokeZeroArgument(callback: unknown) {
  if (typeof callback === 'function') Reflect.apply(callback, undefined, []);
}

/**
 * FPL buttons only expose two heights: `md` (24px) and `lg` (32px). Legacy
 * callsites size the toggle through `btnH`, so we map the requested pixel
 * height onto the nearest FPL size, using `lg` once the height reaches 32px.
 */
function resolveFplButtonSize(btnH: string | number | undefined): 'md' | 'lg' {
  if (btnH == null) return 'md';
  const height = typeof btnH === 'number' ? btnH : Number.parseFloat(btnH);
  if (Number.isNaN(height)) return 'md';
  return height >= 32 ? 'lg' : 'md';
}

export const AppToggleButton = forwardRef<HTMLButtonElement, AppToggleButtonProps>(function AppToggleButton(
  { isFigmaTheme, iconPrefix, children, ...props },
  ref,
) {
  const isFplMigrationEnabled = useFplMigrationFlag();
  const { value: isFigmaThemeEnabled } = useFeatureFlag(FF_FIGMA_THEME);
  // Weave-in-Figma always uses the black/white theme; standalone Weavy keeps the flag.
  const isFigmaEmbed = useIsFigmaEmbedMode();

  const renderLegacy = () => (
    <StyledAppToggleButton
      {...props}
      ref={ref}
      isFigmaThemeResolved={isFigmaTheme ?? (isFigmaThemeEnabled === true || isFigmaEmbed)}
    >
      {iconPrefix}
      {children}
    </StyledAppToggleButton>
  );

  if (!isFplMigrationEnabled) {
    return renderLegacy();
  }

  const { disabled, onClick } = props;
  const resolvedSize = resolveFplButtonSize(props.btnH);
  const opensPopup = props['aria-haspopup'] != null && props['aria-haspopup'] !== false;
  const handleClick = onClick
    ? (event: MouseEvent<HTMLButtonElement>) => Reflect.apply(onClick, undefined, [event, props.value])
    : undefined;

  if (iconPrefix || (opensPopup && !props.isIcon)) {
    return (
      <FplButton
        {...getFplDataAttributes(props)}
        ref={ref}
        variant="ghost"
        size={resolvedSize}
        disabled={disabled}
        onClick={handleClick}
        aria-label={props['aria-label']}
        aria-expanded={Boolean(props.selected)}
        aria-haspopup={props['aria-haspopup']}
        aria-controls={props['aria-controls']}
        iconPrefix={iconPrefix}
      >
        {children}
      </FplButton>
    );
  }

  if (opensPopup && props.isIcon) {
    const ariaLabel = props['aria-label'];
    if (!ariaLabel) return renderLegacy();

    return (
      <DialogTriggerButton
        {...getFplDataAttributes(props)}
        ref={ref}
        size={resolvedSize}
        disabled={disabled}
        onClick={handleClick}
        aria-label={ariaLabel}
        aria-expanded={Boolean(props.selected)}
        aria-haspopup={props['aria-haspopup']}
        aria-controls={props['aria-controls']}
      >
        {children}
      </DialogTriggerButton>
    );
  }

  // FPL ToggleButton is input-backed. Preserve the existing HTMLButtonElement ref
  // contract by leaving ref-using consumers on the exact legacy implementation.
  if (ref != null) return renderLegacy();

  const { onChange, selected } = props;
  const ariaLabel = props['aria-label'];
  if (!ariaLabel) return renderLegacy();

  return (
    <FplToggleButton
      {...getFplDataAttributes(props)}
      checked={selected ?? false}
      size={resolvedSize}
      disabled={disabled}
      onChange={() => {
        invokeZeroArgument(onChange);
        invokeZeroArgument(onClick);
      }}
      aria-label={ariaLabel}
      onIcon={children}
      offIcon={children}
    />
  );
});
